import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  fetchBillingSummary,
  fetchComplianceCredits,
} from "../services/compliance";

export type ReviewCreditsContextValue = {
  reviewCredits: number;
  billingReady: boolean;
  isUnlimited: boolean;
  refreshCredits: () => Promise<void>;
  applyFromScanResult: (credit_balance?: number) => void;
};

const ReviewCreditsContext = createContext<ReviewCreditsContextValue | null>(
  null,
);

/** Studio /studio/* 内共享额度，扫描后顶栏与子页同步。 */
export function ReviewCreditsProvider({ children }: { children: ReactNode }) {
  const [reviewCredits, setReviewCredits] = useState(0);
  const [billingReady, setBillingReady] = useState(false);
  const [isUnlimited, setIsUnlimited] = useState(false);

  const load = useCallback(async () => {
    const credits = await fetchComplianceCredits();
    if (credits) {
      setReviewCredits(credits.credit_balance);
      setIsUnlimited(Boolean(credits.is_unlimited));
      setBillingReady(true);
      return;
    }

    const summary = await fetchBillingSummary();
    if (summary) {
      setReviewCredits(summary.credit_balance);
      setIsUnlimited(Boolean(summary.is_unlimited));
    } else {
      setIsUnlimited(false);
    }
    setBillingReady(true);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFromScanResult = useCallback(
    (credit_balance?: number) => {
      if (typeof credit_balance === "number") {
        setReviewCredits(credit_balance);
        return;
      }
      void load();
    },
    [load],
  );

  const value = useMemo(
    () => ({
      reviewCredits,
      billingReady,
      isUnlimited,
      refreshCredits: load,
      applyFromScanResult,
    }),
    [
      reviewCredits,
      billingReady,
      isUnlimited,
      load,
      applyFromScanResult,
    ],
  );

  return (
    <ReviewCreditsContext.Provider value={value}>
      {children}
    </ReviewCreditsContext.Provider>
  );
}

export function useReviewCredits(): ReviewCreditsContextValue {
  const ctx = useContext(ReviewCreditsContext);
  if (!ctx) {
    throw new Error("useReviewCredits must be used within ReviewCreditsProvider");
  }
  return ctx;
}
